import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  Search, 
  Filter, 
  Eye, 
  Reply, 
  Trash2, 
  Download, 
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  Archive,
  User,
  Calendar,
  Globe,
  Send,
  X,
  Loader,
  AlertCircle,
  FileText
} from 'lucide-react';

interface ContactSubmission {
  id: number;
  name: string;
  email: string;
  country: string | null;
  message: string;
  agree: boolean;
  submitted_at: string;
  ip_address: string;
  status: 'new' | 'read' | 'replied' | 'archived';
  admin_notes: string | null;
  replied_at: string | null;
  replied_by: number | null;
  replied_by_username: string | null;
}

interface SubmissionsResponse {
  success: boolean;
  submissions: ContactSubmission[];
  total_count: number;
  page: number;
  limit: number;
  has_more: boolean;
  status_counts: Record<string, number>;
}

interface FilterState {
  search: string;
  status: string;
  date_from: string;
  date_to: string;
  sort_by: string;
  sort_order: 'asc' | 'desc';
}

const AdminContactSubmissions: React.FC = () => {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const limit = 20;
  
  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: '',
    date_from: '',
    date_to: '',
    sort_by: 'submitted_at',
    sort_order: 'desc'
  });
  
  // Reply form state
  const [replyForm, setReplyForm] = useState({
    subject: '',
    message: ''
  });
  
  // Status update state
  const [statusUpdate, setStatusUpdate] = useState({
    status: '',
    admin_notes: ''
  });

  useEffect(() => {
    loadSubmissions();
  }, [page, filters]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadSubmissions(false); // Silent refresh
    }, 30000);
    
    return () => clearInterval(interval);
  }, [page, filters]);

  const loadSubmissions = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filters
      });
      
      const response = await fetch(`/api/admin/submissions?${params}`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data: SubmissionsResponse = await response.json();
      
      if (data.success) {
        setSubmissions(data.submissions);
        setTotalCount(data.total_count);
        setHasMore(data.has_more);
        setStatusCounts(data.status_counts || {});
      } else {
        throw new Error('Failed to load submissions');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load submissions';
      setError(errorMessage);
      console.error('Failed to load submissions:', err);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const updateSubmissionStatus = async (submissionId: number, updates: Partial<ContactSubmission>) => {
    setIsUpdating(true);
    try {
      const response = await fetch(`/api/admin/submissions/${submissionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update submission');
      }
      
      await loadSubmissions(false);
      
      // Update selected submission if it's the one being updated
      if (selectedSubmission && selectedSubmission.id === submissionId) {
        setSelectedSubmission({ ...selectedSubmission, ...updates });
      }
    } catch (err) {
      console.error('Failed to update submission:', err);
      // You might want to show a toast notification here
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteSubmission = async (submissionId: number) => {
    if (!confirm('Are you sure you want to delete this submission? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/admin/submissions/${submissionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete submission');
      }
      
      await loadSubmissions(false);
      setShowDetailModal(false);
    } catch (err) {
      console.error('Failed to delete submission:', err);
    }
  };

  const sendReply = async () => {
    if (!selectedSubmission || !replyForm.subject.trim() || !replyForm.message.trim()) {
      return;
    }
    
    setIsReplying(true);
    try {
      const response = await fetch(`/api/admin/submissions/${selectedSubmission.id}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          subject: replyForm.subject.trim(),
          message: replyForm.message.trim()
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to send reply');
      }
      
      // Reset form
      setReplyForm({ subject: '', message: '' });
      setShowReplyModal(false);
      
      // Reload submissions
      await loadSubmissions(false);
      
      // Update selected submission
      setSelectedSubmission(prev => prev ? { ...prev, status: 'replied' } : null);
    } catch (err) {
      console.error('Failed to send reply:', err);
    } finally {
      setIsReplying(false);
    }
  };

  const exportSubmissions = async () => {
    try {
      const params = new URLSearchParams({
        ...filters
      });
      
      const response = await fetch(`/api/admin/submissions/export?${params}`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to export submissions');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `contact_submissions_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to export submissions:', err);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      new: { color: 'bg-red-500/20 text-red-200 border-red-500/40', icon: MessageSquare, label: 'New' },
      read: { color: 'bg-yellow-500/20 text-yellow-200 border-yellow-500/40', icon: Eye, label: 'Read' },
      replied: { color: 'bg-green-500/20 text-green-200 border-green-500/40', icon: CheckCircle, label: 'Replied' },
      archived: { color: 'bg-gray-500/20 text-gray-200 border-gray-500/40', icon: Archive, label: 'Archived' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.new;
    const IconComponent = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border ${config.color}`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      date_from: '',
      date_to: '',
      sort_by: 'submitted_at',
      sort_order: 'desc'
    });
    setPage(1);
  };

  const openDetailModal = async (submission: ContactSubmission) => {
    setSelectedSubmission(submission);
    setStatusUpdate({
      status: submission.status,
      admin_notes: submission.admin_notes || ''
    });
    setShowDetailModal(true);
    
    // Mark as read if it's new
    if (submission.status === 'new') {
      await updateSubmissionStatus(submission.id, { status: 'read' });
    }
  };

  const openReplyModal = (submission: ContactSubmission) => {
    setSelectedSubmission(submission);
    setReplyForm({
      subject: `Re: Your inquiry to CIPHER Security`,
      message: `Hi ${submission.name},\n\nThank you for contacting CIPHER Security.\n\nBest regards,\nCIPHER Security Team`
    });
    setShowReplyModal(true);
  };

  if (loading && submissions.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-6">
        <div className="bg-[#0a0a0a] backdrop-blur-sm rounded-xl p-8 border border-white/[0.08] text-center">
          <Loader className="w-8 h-8 text-[#00a86b] mx-auto mb-4 animate-spin" />
          <p className="text-white text-lg">Loading contact submissions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-6">
        <div className="bg-[#0a0a0a] border border-red-500/40 rounded-xl p-8 text-center">
          <AlertCircle className="w-8 h-8 text-red-300 mx-auto mb-4" />
          <p className="text-red-200 text-lg mb-4">{error}</p>
          <button
            onClick={() => loadSubmissions()}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-white mb-1">Contact Submissions</h1>
            <p className="text-sm text-gray-400">
              {totalCount} total submissions
              {Object.entries(statusCounts).length > 0 && (
                <span className="ml-2 text-gray-500">
                  ({Object.entries(statusCounts).map(([status, count]) => 
                    `${count} ${status}`
                  ).join(', ')})
                </span>
              )}
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-3 py-2 bg-[#0a0a0a] hover:bg-white/10 text-white rounded-lg transition-colors border border-white/[0.08]"
            >
              <Filter className="w-4 h-4 mr-2" />
              <span className="text-sm">Filters</span>
            </button>
            
            <button
              onClick={exportSubmissions}
              className="flex items-center px-3 py-2 bg-[#0a0a0a] hover:bg-white/10 text-white rounded-lg transition-colors border border-white/[0.08]"
            >
              <Download className="w-4 h-4 mr-2" />
              <span className="text-sm">Export CSV</span>
            </button>
            
            <button
              onClick={() => loadSubmissions()}
              className="flex items-center px-3 py-2 bg-[#00a86b]/20 hover:bg-[#00a86b]/30 text-[#00a86b] rounded-lg transition-colors border border-[#00a86b]/40"
            >
              <RotateCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-sm">Refresh</span>
            </button>
          </div>
        </div>
        
        {/* Status Filter Badges */}
        <div className="flex items-center space-x-2 flex-wrap">
          <button
            onClick={() => handleFilterChange('status', '')}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
              filters.status === '' 
                ? 'bg-white/20 text-white border border-white/30' 
                : 'bg-[#0a0a0a] text-gray-300 hover:bg-white/10 hover:text-white border border-white/[0.08]'
            }`}
          >
            All ({totalCount})
          </button>
          {Object.entries(statusCounts).map(([status, count]) => (
            <button
              key={status}
              onClick={() => handleFilterChange('status', status)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                filters.status === status 
                  ? 'bg-white/20 text-white border border-white/30' 
                  : 'bg-[#0a0a0a] text-gray-300 hover:bg-white/10 hover:text-white border border-white/[0.08]'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)} ({count})
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mb-6 overflow-hidden"
          >
            <div className="bg-[#0a0a0a] backdrop-blur-sm rounded-xl p-4 border border-white/[0.08]">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      placeholder="Search emails or messages..."
                      className="w-full pl-10 pr-4 py-2 bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-white/[0.15]"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">From Date</label>
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-white/[0.15]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">To Date</label>
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-white/[0.15]"
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Submissions Table */}
      <div className="bg-[#0a0a0a] backdrop-blur-sm rounded-xl border border-white/[0.08] overflow-hidden">
        {submissions.length === 0 ? (
          <div className="p-8 text-center">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-300 text-lg">No submissions found</p>
            {filters.search || filters.status || filters.date_from || filters.date_to ? (
              <p className="text-gray-400 text-sm mt-2">Try adjusting your filters</p>
            ) : null}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0a0a0a] border-b border-white/[0.08]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Message Preview</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.05]">
                {submissions.map((submission) => (
                  <motion.tr
                    key={submission.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-800/30 transition-colors"
                  >
                    <td className="px-4 py-3">
                      {getStatusBadge(submission.status)}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-white text-sm font-medium">{submission.name}</div>
                        <div className="text-gray-400 text-xs">{submission.email}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-300 text-xs line-clamp-2 max-w-md">
                        {submission.message.length > 80 
                          ? `${submission.message.substring(0, 80)}...`
                          : submission.message
                        }
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {formatDate(submission.submitted_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => openDetailModal(submission)}
                          className="p-1.5 text-white hover:bg-white/10 rounded-md transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openReplyModal(submission)}
                          className="p-1.5 text-[#00a86b] hover:bg-[#00a86b]/20 rounded-md transition-colors"
                          title="Reply"
                        >
                          <Reply className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteSubmission(submission.id)}
                          className="p-1.5 text-red-400 hover:bg-red-400/20 rounded-md transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalCount > limit && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-gray-400 text-sm">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalCount)} of {totalCount} submissions
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            <span className="text-white text-sm px-4 py-2">
              Page {page} of {Math.ceil(totalCount / limit)}
            </span>
            
            <button
              onClick={() => setPage(page + 1)}
              disabled={!hasMore}
              className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedSubmission && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#0a0a0a] rounded-xl border border-white/[0.08] w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-white/[0.08]">
                <h3 className="text-xl font-bold text-white">Submission Details</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-4">Contact Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-3" />
                        <span className="text-gray-300">Name: </span>
                        <span className="text-white ml-2">{selectedSubmission.name}</span>
                      </div>
                      <div className="flex items-center">
                        <Mail className="w-4 h-4 text-gray-400 mr-3" />
                        <span className="text-gray-300">Email: </span>
                        <span className="text-white ml-2">{selectedSubmission.email}</span>
                      </div>
                      {selectedSubmission.country && (
                        <div className="flex items-center">
                          <Globe className="w-4 h-4 text-gray-400 mr-3" />
                          <span className="text-gray-300">Country: </span>
                          <span className="text-white ml-2">{selectedSubmission.country}</span>
                        </div>
                      )}
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-3" />
                        <span className="text-gray-300">Submitted: </span>
                        <span className="text-white ml-2">{formatDate(selectedSubmission.submitted_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-4">Status & Management</h4>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <span className="text-gray-300 mr-3">Status:</span>
                        {getStatusBadge(selectedSubmission.status)}
                      </div>
                      
                      {selectedSubmission.replied_at && (
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 text-gray-400 mr-3" />
                          <span className="text-gray-300">Replied: </span>
                          <span className="text-white ml-2">{formatDate(selectedSubmission.replied_at)}</span>
                          {selectedSubmission.replied_by_username && (
                            <span className="text-gray-400 ml-2">by {selectedSubmission.replied_by_username}</span>
                          )}
                        </div>
                      )}
                      
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                        <select
                          value={statusUpdate.status}
                          onChange={(e) => setStatusUpdate(prev => ({ ...prev, status: e.target.value }))}
                          className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-white/[0.15]"
                        >
                          <option value="new">New</option>
                          <option value="read">Read</option>
                          <option value="replied">Replied</option>
                          <option value="archived">Archived</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Message */}
                <div>
                  <h4 className="text-lg font-semibold text-white mb-4">Message</h4>
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <p className="text-gray-300 whitespace-pre-wrap">{selectedSubmission.message}</p>
                  </div>
                </div>
                
                {/* Admin Notes */}
                <div>
                  <h4 className="text-lg font-semibold text-white mb-4">Admin Notes</h4>
                  <textarea
                    value={statusUpdate.admin_notes}
                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, admin_notes: e.target.value }))}
                    placeholder="Add internal notes about this submission..."
                    rows={4}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-[#00a86b] resize-none"
                  />
                </div>
                
                {/* Technical Details */}
                <div>
                  <h4 className="text-lg font-semibold text-white mb-4">Technical Details</h4>
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Submission ID:</span>
                      <span className="text-gray-300">{selectedSubmission.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">IP Address:</span>
                      <span className="text-gray-300">{selectedSubmission.ip_address}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Privacy Agreed:</span>
                      <span className="text-gray-300">{selectedSubmission.agree ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-6 border-t border-white/10">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => openReplyModal(selectedSubmission)}
                    className="flex items-center px-4 py-2 bg-[#00a86b] hover:bg-[#00c77d] text-white rounded-lg transition-colors"
                  >
                    <Reply className="w-4 h-4 mr-2" />
                    Reply
                  </button>
                  
                  <button
                    onClick={() => deleteSubmission(selectedSubmission.id)}
                    className="flex items-center px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </button>
                </div>
                
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                  >
                    Close
                  </button>
                  
                  <button
                    onClick={() => updateSubmissionStatus(selectedSubmission.id, {
                      status: statusUpdate.status as 'new' | 'read' | 'replied' | 'archived',
                      admin_notes: statusUpdate.admin_notes || null
                    })}
                    disabled={isUpdating}
                    className="flex items-center px-4 py-2 bg-[#00a86b] hover:bg-[#00c77d] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdating ? (
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Update
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reply Modal */}
      <AnimatePresence>
        {showReplyModal && selectedSubmission && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowReplyModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#0a0a0a] rounded-xl border border-white/[0.08] w-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-white/[0.08]">
                <h3 className="text-xl font-bold text-white">Reply to {selectedSubmission.name}</h3>
                <button
                  onClick={() => setShowReplyModal(false)}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">To</label>
                  <input
                    type="email"
                    value={selectedSubmission.email}
                    readOnly
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-gray-400 cursor-not-allowed"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Subject</label>
                  <input
                    type="text"
                    value={replyForm.subject}
                    onChange={(e) => setReplyForm(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-[#00a86b]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                  <textarea
                    value={replyForm.message}
                    onChange={(e) => setReplyForm(prev => ({ ...prev, message: e.target.value }))}
                    rows={10}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-[#00a86b] resize-none"
                  />
                </div>
                
                {/* Original Message */}
                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-2">Original Message</h4>
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <p className="text-gray-400 text-sm whitespace-pre-wrap">{selectedSubmission.message}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-white/10">
                <button
                  onClick={() => setShowReplyModal(false)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                
                <button
                  onClick={sendReply}
                  disabled={isReplying || !replyForm.subject.trim() || !replyForm.message.trim()}
                  className="flex items-center px-4 py-2 bg-[#00a86b] hover:bg-[#00c77d] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isReplying ? (
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Reply
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminContactSubmissions;